import { usePerms } from 'contexts/PermsContext';
import { PersonnelContext } from 'features/Responder/features/Personnel';
import { useContext, useEffect, useState } from 'react';
import { getUserProfileInfo } from 'services/user/user.service';

/**
 * Hook to get the current user's ID from personnel context or user profile.
 */
export function useCurrentUserId() {
  const personnelContext = useContext(PersonnelContext);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const perms = usePerms();

  // Only allow if user has ViewUsers permission
  const canViewUser = perms?.testPermission
    ? perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUsers'])
    : false;

  useEffect(() => {
    if (!canViewUser) {
      setCurrentUserId(null);
      setLoading(false);
      return;
    }

    // Try to get user ID from personnel context first
    const contextUserId = personnelContext?.currentUserId;
    if (contextUserId) {
      setCurrentUserId(contextUserId);
      setLoading(false);
      return;
    }

    // Fall back to fetching from user profile
    let isCancelled = false;

    const fetchUserId = async () => {
      try {
        const response = await getUserProfileInfo();
        const profile = response?.data?.result || response?.data;

        if (!isCancelled) {
          const userId = profile?.user_id || profile?.id;
          setCurrentUserId(userId || null);
          setLoading(false);
        }
      } catch (error) {
        console.error('[useCurrentUserId] Failed to fetch user ID:', error);
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchUserId();

    return () => {
      isCancelled = true;
    };
  }, [personnelContext, canViewUser]);

  return { currentUserId, loading };
}
